import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Radar,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  Target,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';

interface ActorTechnique { id: string; name: string }
interface ActorSoftware { name: string; kind: string }
interface ActorCve { id: string; product: string; note: string }

export interface OperationalActor {
  id: string;
  name: string;
  mitreId: string;
  aliases: string[];
  techniques: ActorTechnique[];
  software: ActorSoftware[];
  cves: ActorCve[];
}

type TargetProduct = 'Microsoft Sentinel' | 'Microsoft Defender for Endpoint';

type ActorIoc = {
  id: string;
  value: string;
  type: string;
  source: string;
  sourceKind: 'stix' | 'kloudynet_ti';
  sourceReportId?: string | null;
  sourceTitle?: string | null;
  sourceUrl?: string | null;
  timestamp?: string | null;
  tags: string[];
  matchedBy: string[];
  confidence: 'high' | 'medium';
};

interface ActorIocSearchResponse {
  ok: true;
  actorId: string;
  actorName: string;
  customerId: string;
  items: ActorIoc[];
  matchedReports: Array<{ id: string; title: string; url: string; date?: string | null; matchedBy: string[] }>;
  sources: { stix: number; kloudynetTi: number };
  searchedAt: string;
  message?: string;
}

interface ExposureItem {
  cveId: string;
  product: string;
  note: string;
  deviceCount: number;
  status: string;
  devices: Array<{ id: string; computerDnsName: string; riskScore: string; exposureLevel: string; osPlatform: string; lastSeen: string }>;
}

interface ExposureResponse {
  ok: true;
  customerId: string;
  actor: { id: string; name: string; mitreId: string };
  targetTenantId?: string;
  tokenTenantId?: string;
  authMode?: string;
  cvesChecked: number;
  exposedCves: number;
  exposedDevices: number;
  items: ExposureItem[];
  warnings: string[];
}

interface HuntPlanResponse {
  ok: true;
  customerId: string;
  actor: { id: string; name: string; mitreId: string };
  message: string;
  note?: string;
}

interface HuntAskResponse {
  ok: true;
  request?: {
    id?: string;
    status?: string;
    externalRequestId?: string | null;
    externalCampaignId?: string | null;
    response?: string | null;
    errorMessage?: string | null;
  };
}

interface BulkValidationResponse {
  ok: boolean;
  summary: { totalRows: number; ready: number; warnings: number; errors: number; duplicates: number };
  targetAuth?: Record<string, unknown>;
  message?: string;
}

interface ActorSubmitResponse {
  ok: boolean;
  summary: { requested: number; succeeded: number; failed: number; skipped: number };
  items?: Array<{ rowNumber: number; indicatorValue: string; indicatorType: string; uploadStatus: string; uploadError?: string | null; remoteId?: string | null }>;
  message?: string;
}

function csvEscape(value: unknown): string {
  const text = String(value ?? '');
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function fmt(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function statusClass(value: string): string {
  const v = value.toLowerCase();
  if (v.includes('success') || v === 'ok' || v === 'completed') return 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200';
  if (v.includes('fail') || v.includes('error')) return 'border-rose-400/25 bg-rose-500/10 text-rose-200';
  return 'border-amber-400/25 bg-amber-500/10 text-amber-100';
}

export function ThreatLandscapeOperationalPanel({ actor }: { actor: OperationalActor }) {
  const { customers, activeCustomerId } = useCustomers();
  const { canCapability } = useAuth();
  const [customerId, setCustomerId] = useState(activeCustomerId && activeCustomerId !== 'default' ? activeCustomerId : '');
  const selectedCustomer = customers.find((customer) => customer.id === customerId);

  const canHuntShortcut = canCapability('threat_intel', 'hunt_actor_in_customer') && canCapability('hunts', 'run_threat_hunt_agent');
  const canExposure = canCapability('threat_intel', 'check_actor_mde_exposure');
  const canSearch = canCapability('threat_intel', 'search_actor_iocs');
  const canSubmitShortcut = canCapability('threat_intel', 'submit_actor_iocs');
  const canValidate = canCapability('threat_intel', 'validate_ioc');
  const canSentinelWrite = canCapability('threat_intel', 'create_sentinel_ioc');
  const canMdeWrite = canCapability('threat_intel', 'create_mde_ioc');

  const [huntBusy, setHuntBusy] = useState(false);
  const [huntResult, setHuntResult] = useState<HuntAskResponse['request'] | null>(null);
  const [exposureBusy, setExposureBusy] = useState(false);
  const [exposure, setExposure] = useState<ExposureResponse | null>(null);
  const [iocBusy, setIocBusy] = useState(false);
  const [iocSearch, setIocSearch] = useState<ActorIocSearchResponse | null>(null);
  const [selectedIocIds, setSelectedIocIds] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<TargetProduct>('Microsoft Sentinel');
  const [mdeAction, setMdeAction] = useState('Audit');
  const [validationBusy, setValidationBusy] = useState(false);
  const [validation, setValidation] = useState<BulkValidationResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitResult, setSubmitResult] = useState<ActorSubmitResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showExposureDetails, setShowExposureDetails] = useState(false);

  useEffect(() => {
    if (activeCustomerId && activeCustomerId !== 'default' && customers.some((customer) => customer.id === activeCustomerId)) setCustomerId(activeCustomerId);
  }, [activeCustomerId, customers]);

  useEffect(() => {
    setHuntResult(null);
    setExposure(null);
    setIocSearch(null);
    setSelectedIocIds(new Set());
    setValidation(null);
    setConfirmOpen(false);
    setSubmitResult(null);
    setMessage(null);
    setError(null);
  }, [actor.id, customerId]);

  useEffect(() => {
    setValidation(null);
    setConfirmOpen(false);
    setSubmitResult(null);
  }, [target, mdeAction, selectedIocIds]);

  const selectedIocs = useMemo(() => (iocSearch?.items ?? []).filter((item) => selectedIocIds.has(item.id)).slice(0, 100), [iocSearch, selectedIocIds]);
  const destinationAllowed = target === 'Microsoft Sentinel' ? canSentinelWrite : canMdeWrite;

  function defaults() {
    return {
      source: `KloudSecure Threat Landscape · ${actor.name}`,
      expirationDays: 30,
      confidence: 80,
      tlp: 'white',
      mdeAction,
      mdeSeverity: 'Medium',
      mdeGenerateAlert: true,
      mdeRecommendedActions: `Review ${actor.name} intelligence context and related endpoint activity before escalation.`,
    };
  }

  function selectedCsv(): string {
    const headers = ['IndicatorValue', 'IndicatorType', 'Name', 'Description', 'Source', 'Tags'];
    const rows = selectedIocs.map((item) => [
      item.value,
      item.type,
      `Threat Landscape · ${actor.name}`,
      `Actor-associated IOC reviewed from KloudSecure Threat Landscape for ${actor.name} (${actor.mitreId}).`,
      item.source || 'KloudSecure Threat Landscape',
      `threat-landscape|${actor.mitreId || actor.id}|${actor.name}`,
    ]);
    return `${headers.map(csvEscape).join(',')}\r\n${rows.map((row) => row.map(csvEscape).join(',')).join('\r\n')}\r\n`;
  }

  async function huntActor() {
    if (!customerId || !canHuntShortcut) return;
    setHuntBusy(true); setError(null); setMessage(null); setHuntResult(null);
    try {
      const plan = await apiFetch<HuntPlanResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.id)}/hunt-plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
      });
      const result = await apiFetch<HuntAskResponse>('/hunts/agent/ask', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, message: plan.message }),
      });
      setHuntResult(result.request ?? null);
      setMessage(`Hunt submitted for ${actor.name}. Open HuntOps → Threat Hunt Agent / Run History to follow the full campaign.`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Threat Hunt Agent submission failed.'); }
    finally { setHuntBusy(false); }
  }

  async function checkExposure() {
    if (!customerId || !canExposure) return;
    setExposureBusy(true); setError(null); setMessage(null);
    try {
      const response = await apiFetch<ExposureResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.id)}/mde-exposure?customerId=${encodeURIComponent(customerId)}`);
      setExposure(response); setShowExposureDetails(true);
      setMessage(`MDE exposure check completed for ${response.cvesChecked} actor-associated CVE(s).`);
    } catch (e) { setExposure(null); setError(e instanceof Error ? e.message : 'MDE exposure check failed.'); }
    finally { setExposureBusy(false); }
  }

  async function searchIocs() {
    if (!customerId || !canSearch) return;
    setIocBusy(true); setError(null); setMessage(null); setValidation(null); setSubmitResult(null); setSelectedIocIds(new Set());
    try {
      const response = await apiFetch<ActorIocSearchResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.id)}/iocs?customerId=${encodeURIComponent(customerId)}&limit=200`);
      setIocSearch(response);
      setMessage(response.message ?? `Found ${response.items.length} actor-associated IoC(s).`);
    } catch (e) { setIocSearch(null); setError(e instanceof Error ? e.message : 'Actor IoC search failed.'); }
    finally { setIocBusy(false); }
  }

  function toggleIoc(id: string) {
    setSelectedIocIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < 100) next.add(id);
      return next;
    });
  }

  function selectFirst100() {
    const items = (iocSearch?.items ?? []).slice(0, 100);
    setSelectedIocIds(new Set(items.map((item) => item.id)));
  }

  async function validateSelected() {
    if (!customerId || !selectedIocs.length || !canValidate) return;
    setValidationBusy(true); setError(null); setMessage(null); setConfirmOpen(false); setSubmitResult(null);
    try {
      const response = await apiFetch<BulkValidationResponse>('/threat-intel/ioc/bulk/validate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          customerId, targetProduct: target, filename: `threat-landscape-${actor.mitreId || actor.id}.csv`, content: selectedCsv(), defaults: defaults(),
        }),
      });
      setValidation(response);
      setConfirmOpen(response.summary.errors === 0 && response.summary.ready + response.summary.warnings > 0);
      setMessage(response.message ?? 'Selected actor IoCs validated.');
    } catch (e) { setValidation(null); setError(e instanceof Error ? e.message : 'IoC validation failed.'); }
    finally { setValidationBusy(false); }
  }

  async function submitSelected() {
    if (!customerId || !selectedIocs.length || !validation || !confirmOpen || !canSubmitShortcut || !destinationAllowed) return;
    setSubmitBusy(true); setError(null); setMessage(null);
    try {
      const response = await apiFetch<ActorSubmitResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.id)}/iocs/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, targetProduct: target, items: selectedIocs, defaults: defaults() }),
      });
      setSubmitResult(response); setConfirmOpen(false);
      setMessage(response.message ?? 'Actor IoC submission completed.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Actor IoC submission failed.'); }
    finally { setSubmitBusy(false); }
  }

  return (
    <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-slate-950 via-slate-950 to-slate-900 p-5 shadow-xl">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300"><Radar size={15} /> Phase 2 · Customer operationalization</div>
          <h4 className="mt-2 text-xl font-black text-white">Turn {actor.name} intelligence into customer-scoped action</h4>
          <p className="mt-2 max-w-4xl text-sm leading-6 text-blue-100/70">Select the customer once, then launch a read-only Hunt Agent campaign, check Defender vulnerability exposure, retrieve source-associated IoCs, validate them and deliberately submit selected indicators to Sentinel or MDE.</p>
        </div>
        <div className="min-w-[320px] rounded-xl border border-white/10 bg-white/5 p-3">
          <label className="text-[10px] font-black uppercase tracking-wide text-blue-100/55">Selected customer</label>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="mt-2 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm font-bold text-white outline-none focus:border-cyan-400/50">
            <option value="">Select customer…</option>
            {customers.filter((customer) => customer.enabled).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
          </select>
          {selectedCustomer && <div className="mt-2 text-[10px] leading-4 text-blue-100/55">Tenant {selectedCustomer.tenantId || '—'} · {selectedCustomer.lighthouseMode ? 'Azure Lighthouse' : 'Customer SPN'}<br />Workspace {selectedCustomer.workspaceName || selectedCustomer.workspaceId || 'Not configured'}</div>}
        </div>
      </div>

      {!customerId && <div className="mt-4 rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-3 text-xs text-amber-100"><b>Customer context required.</b> Phase 2 operations never use the MSSP/default tenant implicitly.</div>}
      {error && <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-100">{error}</div>}
      {message && <div className="mt-4 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-100">{message}</div>}

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        <ActionCard icon={Target} title="Hunt this actor" description="Build a bounded actor-aware prompt and submit it to the existing customer-scoped Threat Hunt Agent." allowed={canHuntShortcut} disabled={!customerId || huntBusy} button={huntBusy ? 'Submitting…' : 'Launch Hunt Agent'} onClick={() => void huntActor()} />
        <ActionCard icon={ShieldAlert} title="Check MDE exposure" description={`Check ${actor.cves.length} source-backed actor CVE relationship(s) against Defender Vulnerability Management.`} allowed={canExposure} disabled={!customerId || exposureBusy} button={exposureBusy ? 'Checking…' : 'Check exposure'} onClick={() => void checkExposure()} />
        <ActionCard icon={Search} title="Search actor IoCs" description="Search actor-linked STIX plus recent Kloudynet TI reports matched by actor name, MITRE ID and aliases." allowed={canSearch} disabled={!customerId || iocBusy} button={iocBusy ? 'Searching…' : 'Search IoCs'} onClick={() => void searchIocs()} />
      </div>

      {huntResult && (
        <div className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-500/5 p-4 text-xs text-blue-100/75">
          <div className="flex flex-wrap items-center gap-2"><span className={`rounded-full border px-2 py-0.5 font-black ${statusClass(huntResult.status || 'submitted')}`}>{huntResult.status || 'submitted'}</span><b className="text-white">Hunt request {huntResult.id || 'created'}</b></div>
          <div className="mt-2">Campaign: {huntResult.externalCampaignId || 'pending'} · External request: {huntResult.externalRequestId || 'pending'}</div>
          {huntResult.errorMessage && <div className="mt-2 text-rose-200">{huntResult.errorMessage}</div>}
        </div>
      )}

      {exposure && (
        <div className="mt-4 rounded-xl border border-violet-400/20 bg-violet-500/5 p-4">
          <button type="button" onClick={() => setShowExposureDetails((value) => !value)} className="flex w-full items-center justify-between gap-3 text-left">
            <div><div className="text-xs font-black uppercase tracking-wide text-violet-200">MDE exposure result</div><div className="mt-1 text-sm font-bold text-white">{exposure.exposedDevices} exposed device(s) · {exposure.exposedCves}/{exposure.cvesChecked} actor CVE(s) present</div></div>
            <ChevronDown size={16} className={`text-violet-200 transition ${showExposureDetails ? 'rotate-180' : ''}`} />
          </button>
          <div className="mt-2 text-[10px] text-violet-100/60">Token tenant {exposure.tokenTenantId || '—'} · Target tenant {exposure.targetTenantId || '—'} · {exposure.authMode || 'customer tenant auth'}</div>
          {showExposureDetails && <div className="mt-3 space-y-2">
            {exposure.warnings.map((warning) => <div key={warning} className="rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"><AlertTriangle size={13} className="mr-1 inline" />{warning}</div>)}
            {exposure.items.map((item) => <div key={item.cveId} className="rounded-lg border border-white/10 bg-slate-950/60 p-3 text-xs"><div className="flex flex-wrap items-center justify-between gap-2"><b className="font-mono text-cyan-200">{item.cveId}</b><span className={`rounded-full border px-2 py-0.5 font-black ${item.deviceCount ? 'border-rose-400/25 bg-rose-500/10 text-rose-200' : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200'}`}>{item.deviceCount} device(s)</span></div><div className="mt-1 text-blue-100/60">{item.product || item.note || 'Source-backed Threat Landscape CVE relationship'}</div>{item.devices.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{item.devices.map((device) => <span key={device.id || device.computerDnsName} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-blue-100/70">{device.computerDnsName || device.id} · {device.riskScore || device.exposureLevel || 'risk n/a'}</span>)}</div>}</div>)}
          </div>}
        </div>
      )}

      {iocSearch && (
        <div className="mt-4 rounded-xl border border-sky-400/20 bg-sky-500/5 p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div><div className="text-xs font-black uppercase tracking-wide text-sky-200">Actor IoC workspace</div><div className="mt-1 text-sm font-bold text-white">{iocSearch.items.length} IoC(s) · {iocSearch.matchedReports.length} matched report(s)</div><div className="mt-1 text-[10px] text-sky-100/55">Synchronized STIX: {iocSearch.sources.stix} · Kloudynet TI: {iocSearch.sources.kloudynetTi} · Searched {fmt(iocSearch.searchedAt)}</div></div>
            <div className="flex flex-wrap gap-2"><button type="button" onClick={selectFirst100} disabled={!iocSearch.items.length} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-blue-100 hover:bg-white/10 disabled:opacity-40">Select first {Math.min(100, iocSearch.items.length)}</button><button type="button" onClick={() => setSelectedIocIds(new Set())} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-blue-100 hover:bg-white/10">Clear</button></div>
          </div>

          <div className="mt-3 max-h-[360px] overflow-auto rounded-xl border border-white/10">
            <table className="w-full min-w-[920px] text-left text-xs">
              <thead className="sticky top-0 bg-slate-950 text-[10px] uppercase tracking-wide text-blue-100/55"><tr><th className="p-2.5">Use</th><th className="p-2.5">Type</th><th className="p-2.5">Indicator</th><th className="p-2.5">Source</th><th className="p-2.5">Matched actor term</th><th className="p-2.5">Seen</th></tr></thead>
              <tbody className="divide-y divide-white/5">{iocSearch.items.map((item) => <tr key={item.id} className={selectedIocIds.has(item.id) ? 'bg-cyan-500/10' : ''}><td className="p-2.5"><input type="checkbox" checked={selectedIocIds.has(item.id)} onChange={() => toggleIoc(item.id)} /></td><td className="p-2.5 font-mono text-cyan-200">{item.type}</td><td className="p-2.5 font-mono text-white break-all">{item.value}</td><td className="p-2.5"><div className="text-blue-100/80">{item.source}</div>{item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[10px] text-cyan-300 hover:underline">{item.sourceTitle || 'source report'} <ExternalLink size={10} /></a>}</td><td className="p-2.5 text-blue-100/65">{item.matchedBy.join(', ') || actor.mitreId}</td><td className="p-2.5 text-blue-100/55">{fmt(item.timestamp)}</td></tr>)}</tbody>
            </table>
            {!iocSearch.items.length && <div className="p-6 text-center text-sm text-blue-100/50">No directly source-associated IoCs were found for this actor in the current STIX + recent Kloudynet TI search window.</div>}
          </div>

          {selectedIocs.length > 0 && (
            <div className="mt-4 rounded-xl border border-white/10 bg-slate-950/60 p-4">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                <div><div className="text-xs font-black uppercase tracking-wide text-blue-100/55">Submission staging</div><div className="mt-1 text-sm font-bold text-white">{selectedIocs.length} selected IoC(s) for {selectedCustomer?.name || customerId}</div><div className="mt-1 text-[10px] text-blue-100/50">A validation pass is mandatory. MDE defaults to <b>Audit</b> here instead of Block; you can explicitly change it before validation.</div></div>
                <div className="flex flex-wrap items-end gap-2"><label className="text-[10px] font-bold text-blue-100/55">Destination<select value={target} onChange={(e) => setTarget(e.target.value as TargetProduct)} className="mt-1 block rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white"><option>Microsoft Sentinel</option><option>Microsoft Defender for Endpoint</option></select></label>{target === 'Microsoft Defender for Endpoint' && <label className="text-[10px] font-bold text-blue-100/55">MDE action<select value={mdeAction} onChange={(e) => setMdeAction(e.target.value)} className="mt-1 block rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white"><option>Audit</option><option>Block</option><option>BlockAndRemediate</option><option>Warn</option><option>Alert</option><option>AlertAndBlock</option><option>Allowed</option></select></label>}<button type="button" onClick={() => void validateSelected()} disabled={!canValidate || validationBusy || !destinationAllowed} className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-3 py-2 text-xs font-black text-slate-950 hover:bg-cyan-400 disabled:opacity-40"><ShieldCheck size={14} />{validationBusy ? 'Validating…' : 'Validate selected'}</button></div>
              </div>

              {validation && <div className="mt-3 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-blue-100/70"><div className="flex flex-wrap gap-3"><span className="text-emerald-200">Ready {validation.summary.ready}</span><span className="text-amber-200">Warnings {validation.summary.warnings}</span><span className="text-rose-200">Errors {validation.summary.errors}</span><span className="text-violet-200">Duplicates {validation.summary.duplicates}</span></div></div>}

              {confirmOpen && validation && (
                <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-500/10 p-4 text-xs text-amber-50">
                  <div className="font-black">Review & confirm customer write</div>
                  <div className="mt-2 leading-5">You are about to submit {validation.summary.ready + validation.summary.warnings} validated actor-associated IoC(s) to <b>{target}</b> for <b>{selectedCustomer?.name || customerId}</b>. {target === 'Microsoft Defender for Endpoint' ? `MDE action: ${mdeAction}.` : 'Sentinel indicators will use the selected customer workspace.'}</div>
                  <div className="mt-3 flex gap-2"><button type="button" onClick={() => void submitSelected()} disabled={submitBusy || !canSubmitShortcut || !destinationAllowed} className="inline-flex items-center gap-2 rounded-lg bg-amber-300 px-3 py-2 font-black text-slate-950 hover:bg-amber-200 disabled:opacity-40"><Send size={14} />{submitBusy ? 'Submitting…' : 'Confirm and submit'}</button><button type="button" onClick={() => setConfirmOpen(false)} className="rounded-lg border border-amber-200/20 px-3 py-2 font-bold">Cancel</button></div>
                  {!canSubmitShortcut && <div className="mt-2 text-[10px]">Missing Permissions Master capability: threat_intel.submit_actor_iocs.</div>}
                </div>
              )}

              {submitResult && <div className={`mt-3 rounded-lg border p-3 text-xs ${submitResult.summary.failed ? 'border-amber-400/25 bg-amber-500/10 text-amber-100' : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100'}`}><CheckCircle2 size={14} className="mr-1 inline" />Succeeded {submitResult.summary.succeeded} · Failed {submitResult.summary.failed} · Skipped {submitResult.summary.skipped}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, title, description, allowed, disabled, button, onClick }: { icon: typeof Target; title: string; description: string; allowed: boolean; disabled: boolean; button: string; onClick: () => void }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-center gap-2"><Icon size={17} className="text-cyan-300" /><div className="text-sm font-black text-white">{title}</div></div>
      <p className="mt-2 min-h-12 text-xs leading-5 text-blue-100/60">{description}</p>
      {allowed ? <button type="button" onClick={onClick} disabled={disabled} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-cyan-400/25 bg-cyan-500/10 px-3 py-2 text-xs font-black text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-40"><RefreshCw size={13} className={button.includes('…') ? 'animate-spin' : ''} />{button}</button> : <div className="mt-3 rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-[10px] text-amber-100">Hidden by Permissions Master.</div>}
    </div>
  );
}
